import { useEffect, useState } from 'react'
import {
  AppDialog,
  Button,
  ConfirmActionModal,
  StatusPill,
  TextInput,
  MiniLoader,
} from '../Common'
import { useNls } from '../../nls/nlsProvider'
import {
  cancelEduEvent,
  deleteEduEvent,
  getEduEventRsvps,
  mapEduEventError,
  updateEduEvent,
  type EduEventItem,
  type EduEventRsvpItem,
} from '../../services/eduEventService'
import { openStorageFile } from '../../services/storageHubService'
import { formatEduDate, formatEduDateTime } from '../../utils/eduFamilyFormat'
import './eduEvents.css'

export interface EduEventDetailDialogProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  event: EduEventItem
  isAdmin: boolean
  canEdit: boolean
  onEdit: (event: EduEventItem) => void
  onRefresh: () => void
}

export const EduEventDetailDialog = ({
  isOpen,
  onClose,
  workspaceId,
  event,
  isAdmin,
  canEdit,
  onEdit,
  onRefresh,
}: EduEventDetailDialogProps) => {
  const { t } = useNls()

  const [rsvps, setRsvps] = useState<EduEventRsvpItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [isLoadingRsvps, setIsLoadingRsvps] = useState<boolean>(false)

  // Cancel Modal State
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false)
  const [cancellationReason, setCancellationReason] = useState<string>('')
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [isCancelling, setIsCancelling] = useState<boolean>(false)

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  // Restore State
  const [isRestoring, setIsRestoring] = useState<boolean>(false)
  const [restoreError, setRestoreError] = useState<string | null>(null)

  // Load RSVPs
  useEffect(() => {
    if (!isOpen || !event?.rsvpEnabled) return

    let isMounted = true
    const loadInitialRsvps = async () => {
      try {
        setIsLoadingRsvps(true)
        const res = await getEduEventRsvps({
          workspaceId,
          eventId: event.id,
          limit: 10,
        })
        if (isMounted) {
          setRsvps(res.rsvps)
          setNextCursor(res.nextCursor)
        }
      } catch {
        // Failed loading RSVPs
      } finally {
        if (isMounted) setIsLoadingRsvps(false)
      }
    }

    void loadInitialRsvps()
    return () => {
      isMounted = false
    }
  }, [isOpen, event?.id, event?.rsvpEnabled, workspaceId])

  const handleLoadMoreRsvps = async () => {
    if (!nextCursor || isLoadingRsvps) return
    try {
      setIsLoadingRsvps(true)
      const res = await getEduEventRsvps({
        workspaceId,
        eventId: event.id,
        cursor: nextCursor,
        limit: 10,
      })
      setRsvps((prev) => [...prev, ...res.rsvps])
      setNextCursor(res.nextCursor)
    } finally {
      setIsLoadingRsvps(false)
    }
  }

  const handleConfirmCancel = async () => {
    const reason = cancellationReason.trim()
    if (!reason) {
      setCancelError(t('NLSEduEventDetailCancelReasonLabel'))
      return
    }

    try {
      setIsCancelling(true)
      setCancelError(null)
      await cancelEduEvent({
        workspaceId,
        eventId: event.id,
        cancellationReason: reason,
      })
      setIsCancelModalOpen(false)
      onRefresh()
    } catch (err: unknown) {
      const errKey = mapEduEventError(err)
      setCancelError(t(errKey))
    } finally {
      setIsCancelling(false)
    }
  }

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true)
      setDeleteError(null)
      await deleteEduEvent({
        workspaceId,
        eventId: event.id,
      })
      setIsDeleteModalOpen(false)
      onRefresh()
      onClose()
    } catch (err: unknown) {
      const errKey = mapEduEventError(err)
      setDeleteError(t(errKey))
    } finally {
      setIsDeleting(false)
    }
  }

  const handleRestore = async () => {
    try {
      setIsRestoring(true)
      setRestoreError(null)
      await updateEduEvent({
        workspaceId,
        eventId: event.id,
        status: 'ACTIVE',
      })
      onRefresh()
    } catch (err: unknown) {
      const errKey = mapEduEventError(err)
      setRestoreError(t(errKey))
    } finally {
      setIsRestoring(false)
    }
  }

  const formatAudienceText = () => {
    if (event.audience.kind === 'ALL') return t('NLSEduEventAudienceAll')
    if (event.audience.kind === 'ROOT') return t('NLSEduEventAudienceRoot')
    return t('NLSEduEventAudienceNodes')
  }

  const formatEventWhen = () => {
    if (event.allDay) {
      const startStr = formatEduDate(event.startAt, undefined, event.timezone)
      const endStr = formatEduDate(event.endAt, undefined, event.timezone)
      return startStr === endStr ? startStr : `${startStr} – ${endStr}`
    }
    const startStr = formatEduDateTime(event.startAt, undefined, event.timezone)
    const endStr = formatEduDateTime(event.endAt, undefined, event.timezone)
    return `${startStr} – ${endStr}`
  }

  return (
    <>
      <AppDialog
        open={isOpen}
        onDismiss={onClose}
        title={t('NLSEduEventDetailTitle')}
        size="lg"
        actions={[
          {
            label: t('NLSEduEventCancelButton'),
            onClick: onClose,
            variant: 'secondary',
          },
        ]}
      >
        <div className="eduEventDetail">
          {/* Cancelled Notice Banner */}
          {event.status === 'CANCELLED' && (
            <div className="eduDialog__banner eduDialog__banner--error">
              <div>
                {t('NLSEduEventDetailCancelledNotice', {
                  reason: event.cancellationReason || '',
                })}
              </div>
              {canEdit && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleRestore}
                  disabled={isRestoring}
                >
                  {t('NLSEduEventDetailRestore')}
                </Button>
              )}
            </div>
          )}

          {restoreError && (
            <div className="eduDialog__banner eduDialog__banner--error" role="alert">
              {restoreError}
            </div>
          )}

          {/* Header & Badges */}
          <div className="eduEventDetail__header">
            <div className="eduEventDetail__headerMain">
              <h3 className="eduEventDetail__title">{event.title}</h3>
              <div className="eduEventDetail__badges">
                <StatusPill tone={event.status === 'ACTIVE' ? 'success' : 'neutral'}>
                  {event.status === 'ACTIVE'
                    ? t('NLSEduEventStatusActive')
                    : t('NLSEduEventStatusCancelled')}
                </StatusPill>
                {event.allDay && (
                  <StatusPill tone="info">
                    {t('NLSEduEventAllDayBadge')}
                  </StatusPill>
                )}
                <span className="eduEventDetail__audienceBadge">{formatAudienceText()}</span>
              </div>
            </div>

            {/* Actions for managers */}
            <div className="eduEventDetail__actions">
              {canEdit && event.status !== 'CANCELLED' && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onEdit(event)}
                >
                  {t('NLSEduEventDetailEdit')}
                </Button>
              )}

              {canEdit && event.status === 'ACTIVE' && (
                <Button
                  variant="secondary"
                  tone="danger"
                  size="sm"
                  onClick={() => {
                    setCancellationReason('')
                    setCancelError(null)
                    setIsCancelModalOpen(true)
                  }}
                >
                  {t('NLSEduEventDetailCancel')}
                </Button>
              )}

              {isAdmin && (
                <Button
                  variant="tertiary"
                  tone="danger"
                  size="sm"
                  onClick={() => {
                    setDeleteError(null)
                    setIsDeleteModalOpen(true)
                  }}
                >
                  {t('NLSEduEventDetailDelete')}
                </Button>
              )}
            </div>
          </div>

          {/* Date, Location, Link */}
          <div className="eduEventDetail__metaGrid">
            <div className="eduEventDetail__metaItem">
              <span className="eduEventDetail__metaLabel">{t('NLSEduEventStartDateLabel')}</span>
              <span className="eduEventDetail__metaValue">{formatEventWhen()}</span>
            </div>

            {event.location && (
              <div className="eduEventDetail__metaItem">
                <span className="eduEventDetail__metaLabel">{t('NLSEduEventLocationLabel')}</span>
                <span className="eduEventDetail__metaValue">{event.location}</span>
              </div>
            )}

            {event.onlineUrl && (
              <div className="eduEventDetail__metaItem">
                <span className="eduEventDetail__metaLabel">{t('NLSEduEventOnlineUrlLabel')}</span>
                <span className="eduEventDetail__metaValue">
                  <a
                    href={event.onlineUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="eduEventDetail__link"
                  >
                    {event.onlineUrl}
                  </a>
                </span>
              </div>
            )}
          </div>

          {/* Description */}
          {event.description && (
            <div className="eduEventDetail__section">
              <span className="eduEventDetail__metaLabel">{t('NLSEduEventDescriptionLabel')}</span>
              <p className="eduEventDetail__description">{event.description}</p>
            </div>
          )}

          {/* Attachments */}
          {event.attachments && event.attachments.length > 0 && (
            <div className="eduEventDetail__section">
              <span className="eduEventDetail__metaLabel">{t('NLSEduEventAttachmentsLabel')}</span>
              <div className="eduEventDetail__attachmentsList">
                {event.attachments.map((a) => (
                  <Button
                    key={a.fileId}
                    variant="secondary"
                    size="sm"
                    onClick={() => void openStorageFile(a.fileId)}
                    aria-label={`${t('NLSEduEventDetailOpenAttachment')}: ${a.name}`}
                  >
                    📎 {a.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* RSVPs */}
          {event.rsvpEnabled && (
            <div className="eduEventDetail__section">
              <div className="eduEventDetail__rsvpHeader">
                <span className="eduEventDetail__metaLabel">{t('NLSEduEventDetailRsvpsTitle')}</span>
                <span className="eduEventDetail__rsvpCounts">
                  {t('NLSEduEventRsvpsCount', {
                    going: event.rsvpCounts.going,
                    maybe: event.rsvpCounts.maybe,
                    notGoing: event.rsvpCounts.notGoing,
                  })}
                </span>
              </div>

              {event.rsvpDeadline && (
                <div className="eduEventDetail__rsvpDeadline">
                  {t('NLSEduEventRsvpDeadlineLabel')}: {formatEduDateTime(event.rsvpDeadline, undefined, event.timezone)}
                </div>
              )}

              {rsvps.length === 0 && !isLoadingRsvps && (
                <div className="eduEventDetail__emptyRsvps">
                  {t('NLSEduEventDetailNoRsvps')}
                </div>
              )}

              {rsvps.length > 0 && (
                <div className="eduEventDetail__rsvpTable">
                  <div className="eduEventDetail__rsvpRow eduEventDetail__rsvpRow--header">
                    <span>{t('NLSEduEventDetailRsvpLearner')}</span>
                    <span>{t('NLSEduEventDetailRsvpStatus')}</span>
                    <span>{t('NLSEduEventDetailRsvpBy')}</span>
                    <span>{t('NLSEduEventDetailRsvpWhen')}</span>
                  </div>
                  {rsvps.map((r) => (
                    <div key={r.id} className="eduEventDetail__rsvpRow">
                      <span className="eduEventDetail__learnerName">
                        {r.learnerName || r.learnerId}
                      </span>
                      <span>
                        <StatusPill
                          tone={
                            r.status === 'GOING'
                              ? 'success'
                              : r.status === 'NOT_GOING'
                                ? 'danger'
                                : 'neutral'
                          }
                        >
                          {r.status === 'GOING'
                            ? t('NLSEduEventDetailRsvpGoing')
                            : r.status === 'NOT_GOING'
                              ? t('NLSEduEventDetailRsvpNotGoing')
                              : t('NLSEduEventDetailRsvpMaybe')}
                        </StatusPill>
                      </span>
                      <span>{r.respondedByName || r.respondedByUid}</span>
                      <span>{formatEduDate(r.respondedAt)}</span>
                    </div>
                  ))}
                </div>
              )}

              {isLoadingRsvps && (
                <div className="eduEventDetail__loader">
                  <MiniLoader />
                </div>
              )}

              {nextCursor && !isLoadingRsvps && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleLoadMoreRsvps}
                >
                  {t('NLSEduEventLoadMore')}
                </Button>
              )}
            </div>
          )}
        </div>
      </AppDialog>

      {/* Cancel Reason Modal */}
      {isCancelModalOpen && (
        <AppDialog
          open={isCancelModalOpen}
          onDismiss={() => setIsCancelModalOpen(false)}
          title={t('NLSEduEventDetailCancelModalTitle')}
          size="md"
          actions={[
            {
              label: t('NLSEduEventCancelButton'),
              onClick: () => setIsCancelModalOpen(false),
              variant: 'secondary',
              disabled: isCancelling,
            },
            {
              label: t('NLSEduEventDetailCancelConfirmButton'),
              onClick: handleConfirmCancel,
              variant: 'primary',
              disabled: isCancelling,
            },
          ]}
        >
          <div className="eduDialog__content">
            {cancelError && (
              <div className="eduDialog__banner eduDialog__banner--error" role="alert">
                {cancelError}
              </div>
            )}
            <span className="eduEventEditor__label">
              {t('NLSEduEventDetailCancelReasonLabel')}
            </span>
            <TextInput
              value={cancellationReason}
              onChange={(val) => setCancellationReason(val)}
              placeholder={t('NLSEduEventDetailCancelReasonPlaceholder')}
              disabled={isCancelling}
            />
          </div>
        </AppDialog>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <ConfirmActionModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          title={t('NLSEduEventDetailDeleteModalTitle')}
          body={deleteError || t('NLSEduEventDetailDeleteConfirmMessage')}
          confirmLabel={t('NLSEduEventDetailDeleteConfirmButton')}
          cancelLabel={t('NLSEduEventCancelButton')}
          danger={true}
          busy={isDeleting}
        />
      )}
    </>
  )
}
